/**
 * Shared utilities for resolving EMS participant photo URLs.
 * Used by ImageApproval, ParticipantEdit, and UserFormView so every screen
 * resolves the same stored value to the same loadable URL.
 */

function backendBaseFromApiUrl(apiUrl) {
  const base = String(apiUrl || '').trim().replace(/\/$/, '');
  if (!base) return '';
  return base.replace(/\/api$/, '');
}

/** Encode each path segment; keep `/` as real separators (avoids Apache %2F 404). */
export function encodeUploadKeyPath(key) {
  return String(key || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

/**
 * Rewrite legacy URLs that encoded the whole S3 key with %2F
 * (e.g. /api/ems/uploads/Accreditation%2F2028%2Ffile.png).
 */
export function rewriteLegacyEncodedUploadsUrl(url) {
  const s = String(url || '').trim();
  if (!s || (!s.includes('%2F') && !s.includes('%2f'))) return s;
  try {
    const absolute = s.startsWith('http://') || s.startsWith('https://');
    const u = absolute ? new URL(s) : new URL(s, 'http://local.invalid');
    const markers = ['/api/ems/uploads/', '/ems/uploads/'];
    for (const marker of markers) {
      const idx = u.pathname.indexOf(marker);
      if (idx < 0) continue;
      const encodedKey = u.pathname.slice(idx + marker.length);
      const key = decodeURIComponent(encodedKey);
      const fixedPath = `${u.pathname.slice(0, idx + marker.length)}${encodeUploadKeyPath(key)}`;
      if (!absolute) return fixedPath + (u.search || '');
      return `${u.origin}${fixedPath}${u.search || ''}`;
    }
  } catch {
    /* ignore */
  }
  return s;
}

/**
 * Converts a stored EMS upload filename/path to a browser-loadable URL.
 * Handles:
 *   - Already-absolute http(s):// URLs → returned as-is (legacy %2F rewritten)
 *   - storage/s3-upload/… paths → prepend backend host
 *   - S3 keys / plain filenames → <API_URL>/ems/uploads/<key-with-slashes>
 */
export function resolveEmsUploadPublicUrl(urlOrFilename, apiUrl) {
  if (urlOrFilename == null || urlOrFilename === '') return '';
  let s = String(urlOrFilename).trim();
  if (!s) return '';

  s = rewriteLegacyEncodedUploadsUrl(s);

  if (s.startsWith('http://') || s.startsWith('https://')) return s;
  const normalized = s.startsWith('storage/s3-upload/') ? `/${s}` : s;

  const backendBase = backendBaseFromApiUrl(apiUrl);
  if (normalized.startsWith('/')) {
    return backendBase ? `${backendBase}${normalized}` : normalized;
  }

  const apiBase = String(apiUrl || '').trim().replace(/\/$/, '');
  const pathKey = encodeUploadKeyPath(normalized);
  return apiBase ? `${apiBase}/ems/uploads/${pathKey}` : `/api/ems/uploads/${pathKey}`;
}

/**
 * Prefer full S3 key from upload API (e.g. Accreditation/2028/…) over filename-only.
 */
export function getPersistedEmsUploadRef(uploadResponseData) {
  if (!uploadResponseData || typeof uploadResponseData !== 'object') return '';
  if (typeof uploadResponseData.key === 'string' && uploadResponseData.key.trim()) {
    return uploadResponseData.key.trim();
  }
  const url = typeof uploadResponseData.url === 'string' ? uploadResponseData.url : '';
  if (!url) return '';
  try {
    const pathname = url.startsWith('http') ? new URL(url).pathname : url;
    const markers = ['/api/ems/uploads/', '/ems/uploads/'];
    for (const marker of markers) {
      const idx = pathname.indexOf(marker);
      if (idx >= 0) {
        return decodeURIComponent(pathname.slice(idx + marker.length));
      }
    }
  } catch {
    /* ignore malformed URL */
  }
  return url.split('/').pop() || '';
}

/**
 * Normalises the `photo` value from person_data so that Formio's file component
 * always receives a fully-qualified URL.
 *
 * After an admin crop, person_data.photo[0].url is stored as a plain filename
 * (e.g. "cropped-1234.png"). This function resolves it to the full public URL so
 * every screen — ParticipantEdit and UserFormView — displays the same image.
 */
export function normalizeAccreditationPhotoForFormio(photoVal, apiUrl) {
  if (photoVal == null) return photoVal;
  const list = Array.isArray(photoVal) ? photoVal : [photoVal];
  if (list.length === 0) return photoVal;

  return list.map((entry) => {
    if (typeof entry === 'string') {
      const s0 = entry.trim();
      if (!s0) return entry;
      return {
        url: resolveEmsUploadPublicUrl(s0, apiUrl),
        name: s0.split('/').pop() || 'photo.png',
        type: 'image/png',
        storage: 'url',
        size: 0,
      };
    }

    if (!entry || typeof entry !== 'object') return entry;

    const rawUrl = entry.url;
    if (rawUrl == null || rawUrl === '') return entry;

    const s = String(rawUrl).trim();
    const fullUrl =
      s.startsWith('http://') || s.startsWith('https://') || s.startsWith('/')
        ? rewriteLegacyEncodedUploadsUrl(s)
        : resolveEmsUploadPublicUrl(s, apiUrl);

    return {
      ...entry,
      url: fullUrl,
      name: entry.name || s.split('/').pop() || 'photo.png',
      type: entry.type || 'image/png',
      storage: entry.storage || 'url',
      size: typeof entry.size === 'number' ? entry.size : Number(entry.size) || 0,
    };
  });
}

/**
 * Same URL derivation for AccreditationPhotoManager in UserFormView and ParticipantEdit:
 * reads `submission.data.photo[0]` (array — never `photo.url` on the parent object).
 * `url` is expected to already be normalized (e.g. via normalizeAccreditationPhotoForFormio);
 * `original_url` is resolved here if still a storage path or filename.
 *
 * @param {object|null|undefined} submissionData — typically `submission.data`
 * @param {string} apiUrl — REACT_APP_API_URL
 * @returns {{ currentPhotoUrl: string|null, originalPhotoUrl: string|null }}
 */
export function getAccreditationPhotoDisplayUrls(submissionData, apiUrl) {
  if (!submissionData || typeof submissionData !== 'object') {
    return { currentPhotoUrl: null, originalPhotoUrl: null };
  }
  const photo = submissionData.photo;
  const first = Array.isArray(photo) ? photo[0] : null;
  if (!first) return { currentPhotoUrl: null, originalPhotoUrl: null };

  if (typeof first === 'object') {
    const currentPhotoUrl = first.url
      ? rewriteLegacyEncodedUploadsUrl(first.url)
      : null;
    const raw = first.original_url;
    if (raw == null || raw === '') {
      return { currentPhotoUrl, originalPhotoUrl: currentPhotoUrl };
    }
    const rs = String(raw).trim();
    const originalPhotoUrl =
      rs.startsWith('http://') || rs.startsWith('https://')
        ? rewriteLegacyEncodedUploadsUrl(rs)
        : resolveEmsUploadPublicUrl(rs, apiUrl);
    return { currentPhotoUrl, originalPhotoUrl };
  }

  const asString = String(first) || null;
  return { currentPhotoUrl: asString, originalPhotoUrl: asString };
}
